/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { ArrowUpRightIcon } from 'lucide-react'
import Image from 'next/image'
import React from 'react'

import { Link } from '@/i18n/navigation'
import { MtaKitStoryblok } from '@/lib/storyblok/types/generated-types'

/** A description of the available properties for this component. */
type KitCardProps = {
  /** The kit to display. */
  kit: MtaKitStoryblok
}

/** Converts an SVG string to a data URI. */
const svgToDataUri = (svgString: string): string => {
  const encoded = encodeURIComponent(svgString)
    .replace(/%20/g, ' ')
    .replace(/%3D/g, '=')
    .replace(/%3A/g, ':')
    .replace(/%2F/g, '/')
    .replace(/%22/g, "'")
  return `data:image/svg+xml,${encoded}`
}

const maskSvgString = `<svg width="242" height="172" viewBox="0 0 242 172" xmlns="http://www.w3.org/2000/svg"><path d="M8 0C3.58172 0 0 3.42253 0 7.64444V164.356C0 168.577 3.58173 172 8 172H186C194.837 172 202 165.155 202 156.711V131.533C202 127.311 205.582 123.888 210 123.888H226C234.837 123.888 242 117.043 242 108.599V7.64444C242 3.42253 238.418 0 234 0H8Z" fill="white"/></svg>`
const svgMaskDataUri = svgToDataUri(maskSvgString)
const svgAspectRatio = 242 / 172

/**
 * A card component for use in the kit overview accordion.
 */
const KitCard: React.FC<KitCardProps> = ({ kit }) => {
  const thumbnail = kit.thumbnail
  const kitColour = kit.colour?.color

  return (
    <Link href={`/kits/${kit.slug}`}>
      <div
        key={kit._uid}
        className='group relative flex w-60 shrink-0 flex-col gap-3 rounded-lg bg-white p-3 md:w-64 md:p-4'
      >
        {thumbnail?.filename && (
          <div className={'relative'}>
            <div
              className='relative w-full'
              style={{
                aspectRatio: `${svgAspectRatio}`,
                maskImage: `url("${svgMaskDataUri}")`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskImage: `url("${svgMaskDataUri}")`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
            >
              <Image src={thumbnail.filename} alt={thumbnail.alt || kit.name} layout='fill' objectFit='cover' />
            </div>
            {kitColour && (
              <div
                className='absolute bottom-1.5 right-0 flex h-8 w-8 items-center justify-center rounded-full'
                style={{ backgroundColor: kitColour }}
              >
                <ArrowUpRightIcon className='h-5 w-5 text-white ' aria-hidden='true' />
              </div>
            )}
          </div>
        )}
        <h3 className='mt-1 text-base font-semibold text-primary-500 md:text-lg group-hover:underline'>{kit.name}</h3>
        {kit.tagline && <p className='text-xs text-gray-600 md:text-sm'>{kit.tagline}</p>}
      </div>
    </Link>
  )
}

export { KitCard }
